import { Link } from 'react-router-dom';
import { ArrowUpRight } from 'lucide-react';
import Seo from '../components/Seo';
import PendingCertification from '../components/PendingCertification';
import CapabilityShowcase from '../components/CapabilityShowcase';
import AudienceSwitcher from '../components/AudienceSwitcher';
import NationalReach from '../components/NationalReach';
import RelatedPages from '../components/RelatedPages';
import { CAPABILITIES } from '../lib/site';
import { HOME_CAPABILITY_LEARNING } from '../lib/homeCapabilityLearning';
import { HomeHero, HomeTrustedStrip, HomeProduction, HomeProductRoutes } from '../components/home/HomeOpening';
import { HomeEvidence } from '../components/home/HomeEvidence';
import { HomeTestimonial, HomeFinalCta } from '../components/home/HomeClosing';
import styles from './Home.module.css';

const HOME_DESCRIPTION = 'U.S. energetics and defense manufacturer in Grand Junction, CO: impulse cartridges, CAD/PAD, initiators, explosives and precision machining. ITAR, ISO 9001.';
// Plain-language exploration is scoped to Home; shared capability records stay intact.
const HOME_CAPABILITIES = CAPABILITIES.map(item => ({ ...item, ...HOME_CAPABILITY_LEARNING[item.code] }));

export default function Home() {
  return <div data-testid="home-page" className={styles.home}>
    <Seo
      title="Energetics & Defense Manufacturer | Valor Tactical"
      description={HOME_DESCRIPTION}
      path="/"
      keywords="defense manufacturer,
defense manufacturing,
U.S. defense manufacturer,
defense manufacturing company,
defense manufacturing services,
defense contractor,
defense engineering,
aerospace and defense manufacturing,
integrated manufacturing,
vertically integrated manufacturing,
contract manufacturing,
production manufacturing,
precision manufacturing,
engineering services,
prototype development,
design for manufacturing,
qualification testing,
qualification and validation,
MIL-STD qualification,
MIL-STD qualification testing,
environmental qualification,
environmental testing,
defense testing,
performance validation,
energetics manufacturing,
defense energetics,
energetic materials,
energetic materials manufacturing,
energetics assembly,
countermeasure manufacturing,
countermeasure cartridges,
propellant charges,
munition manufacturing,
munition components,
hermetic sealing,
glass-to-metal sealing,
bridgewire welding,
electromechanical integration,
mechanical assemblies,
hermetic assemblies,
lot traceability,
quality management,
ISO 9001,
ISO 14001,
ITAR registered,
CMMC Level 2,
CAGE Code 04099,
defense supply chain,
prime contractor supplier,
government manufacturing,
DoD supplier,
U.S. defense industry,
Grand Junction Colorado,
Colorado defense manufacturer,
Western Colorado manufacturing,
CCI Capco,
CCI Capco LLC,
Capco,
Capco LLC,
legacy Capco"
      jsonLd={[
        {
          '@context': 'https://schema.org', '@type': ['Organization', 'LocalBusiness'],
          '@id': 'https://valortactical.us#organization', name: 'Valor Tactical Innovation',
          alternateName: ['Valor Tactical', 'Valor Tactical Innovation LLC', 'CCI Capco', 'CCI Capco LLC', 'Capco', 'Capco LLC'],
          legalName: 'Valor Tactical Innovation LLC', url: 'https://valortactical.us/',
          logo: 'https://valortactical.us/favicon-512.png', description: HOME_DESCRIPTION,
          address: { '@type': 'PostalAddress', streetAddress: '1328 Winters Ave', addressLocality: 'Grand Junction', addressRegion: 'CO', postalCode: '81501', addressCountry: 'US' },
          identifier: [
            { '@type': 'PropertyValue', propertyID: 'CAGE', value: '04099' },
          ],
          hasCertification: [
            { '@type': 'Certification', name: 'CMMC Level 2' },
            { '@type': 'Certification', name: 'ISO 9001' },
            { '@type': 'Certification', name: 'ISO 14001' },
          ],
          brand: { '@type': 'Brand', name: 'Valor Tactical' },
          telephone: '+1-970-243-8750',
          openingHoursSpecification: [{ '@type': 'OpeningHoursSpecification', dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday'], opens: '07:00', closes: '17:30' }],
          hasMap: 'https://www.google.com/search?kgmid=/g/11sf14_sk5',
          sameAs: ['https://www.linkedin.com/company/valortactical', 'https://www.google.com/search?kgmid=/g/11sf14_sk5'],
        },
        {
          '@context': 'https://schema.org', '@type': 'WebSite', '@id': 'https://valortactical.us#website',
          name: 'Valor Tactical', url: 'https://valortactical.us/', publisher: { '@id': 'https://valortactical.us#organization' },
        },
      ]}
    />
    <div className={styles.topCertification} data-testid="home-top-certification"><PendingCertification prominent animate /></div>
    <HomeHero />
    <HomeProductRoutes />
    <HomeTrustedStrip />
    <HomeProduction />

    <section className={`${styles.section} ${styles.capabilities}`} data-testid="capabilities-section">
      <div className={styles.container}>
        <Link to="/capabilities" className={styles.capabilityHeadingLink} data-testid="capabilities-section-heading">
          <h2 className="font-display font-bold text-4xl md:text-5xl lg:text-6xl text-navy leading-[1.05]" data-testid="home-capability-title"><span className={styles.capabilityHeadingFirst}>Manufacturing<span className={styles.capabilityHeadingLine} aria-hidden="true" /></span> <span className={styles.accent}>capabilities</span></h2>
          <p className={styles.lead} data-testid="home-capability-instruction">Choose a capability to learn what it does, then explore the details.</p>
          <span className={styles.textLink}>View the full matrix<ArrowUpRight size={18} aria-hidden="true" /></span>
        </Link>
        <CapabilityShowcase sequential items={HOME_CAPABILITIES} />
        <nav aria-label="Energetic product families" className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-2 text-navy text-base md:text-lg" data-testid="home-energetics-families"><span className="font-mono text-[13px] tracking-[0.2em] uppercase text-steel">Energetic product families</span><Link to="/energetics/cad-pad" className="underline underline-offset-4 hover:text-steel">CAD/PAD manufacturing</Link><Link to="/energetics/impulse-cartridges" className="underline underline-offset-4 hover:text-steel">Impulse cartridges</Link><Link to="/energetics/initiators-squibs-eeds" className="underline underline-offset-4 hover:text-steel">Initiators, squibs &amp; EEDs</Link></nav>
      </div>
    </section>

    <section className={`${styles.section} ${styles.audiences}`} data-testid="audiences-section">
      <div className={styles.container}>
        <h2 className="font-display font-bold text-4xl md:text-5xl lg:text-6xl text-white leading-[1.05]" data-testid="home-audience-title">Support for your <span className={styles.accent}>program team</span></h2>
        <div className={styles.audienceGuide}>
          <AudienceSwitcher sequential descriptions={{ primes: 'Scalable production for energetics and mechanical assemblies, with qualification support and capacity for surge demand.' }} />
        </div>
      </div>
    </section>

    <HomeEvidence />
    <HomeTestimonial />
    <NationalReach />
    <RelatedPages current="/" />
    <HomeFinalCta />
    <aside className={styles.mediaCredits} aria-label="Image credits">
      <p>Production-section photograph: <span>U.S. Air Force / Senior Airman Julianne Showalter, via DVIDS</span>. Illustrative aircraft countermeasure deployment.</p>
      <p>The appearance of U.S. Department of War (DoW) visual information does not imply or constitute DoW endorsement.</p>
    </aside>
  </div>;
}